// Kiểu dữ liệu SINH TỪ RESPONSE THẬT của máy chủ, bắt qua CDP ngày 14/09/2026.
// Không tự nghĩ ra cấu trúc — mọi trường ở đây đều đã quan sát được trên
// https://fbmax.net với Extension kết nối thật.

// ===== Phiên đăng nhập =====
export interface NguoiDung {
  name: string;
  email: string;
  role: string;
  tenantId: string;
}

export interface HanMuc {
  maxFbAccounts: number | null;
  maxGroups: number | null;
  maxPostsPerDay: number | null;
}

/**
 * Quyền lợi theo gói.
 *
 * NHIỀU TRƯỜNG CÓ THỂ `null` — máy chủ trả đúng như vậy khi khách chưa từng mua
 * (paymentStore.js:190 trả `planId: null, expiresAt: null, daysLeft: null`).
 * Khai báo chúng là `string`/`number` đặc là nói dối chính mình: TypeScript sẽ
 * để lọt `quyen.planName.slice(...)` rồi nổ lúc chạy ở đúng người dùng mới.
 */
export interface QuyenLoi {
  active: boolean;
  planId: string | null;
  planName?: string | null;
  status: string;
  activatedAt?: string | null;
  expiresAt: string | null;
  daysLeft: number | null;
  limits: HanMuc;
  usage?: {
    postsToday: number;
    postsPerDay: number | null;
    postsRemaining: number | null;
  };
}

// ===== Gói dịch vụ =====
export interface Goi {
  id: string;
  name: string;
  /** `null` = chưa chốt giá. KHÔNG hiển thị như "miễn phí". */
  amount: number | null;
  period: string;
  durationDays: number | null;
  limits: HanMuc;
  available: boolean;
}

export interface ThongTinNganHang {
  bankId: string;
  accountNo: string;
  accountName: string;
}

export interface DonHang {
  id: string;
  orderCode: string;
  transferContent: string;
  status: 'PENDING' | 'ACTIVATED' | 'UNDERPAID' | 'SUPERSEDED' | 'CANCELLED' | string;
  planId: string;
  planName: string;
  period: string;
  amount: number | null;
  createdAt: string;
  expiresAt: string | null;
  bank: ThongTinNganHang;
  /** Ảnh QR VietQR dựng sẵn; `null` khi gói giá 0. */
  qrUrl: string | null;
}

export interface Phien {
  ok: boolean;
  token: string;
  user: NguoiDung;
  entitlement: QuyenLoi;
}

// ===== Tài khoản Facebook =====
export interface TaiKhoanFB {
  uid: string;
  name: string;
  avatar: string;
  isPage: boolean;
  mainUid: string | null;
  mainName: string | null;
  deviceId: string;
  version: string;
  build?: string | null;
  identityState?: 'VERIFIED' | 'UNAVAILABLE';
  identityContext?: { state: 'UNVERIFIED' | 'VERIFYING' | 'READY' | 'STALE' | 'CONFLICT' | 'OFFLINE'; actorUid: string | null; ownerUid: string | null; actorKind: 'PERSONAL' | 'PAGE' | null; epoch: string | null; deviceId: string; name: string | null } | null;
  updatedAt: string;
}

export interface TrangThaiExtension {
  ok: boolean;
  connected: boolean;
  facebookLoggedIn: boolean;
  uid: string | null;
  devices: number;
  accounts: TaiKhoanFB[];
}

// ===== MCP Agent =====
export type McpScope = 'account:read' | 'groups:read' | 'jobs:read' | 'jobs:write' | 'posts:propose' | 'comments:propose' | 'collections:read' | 'campaigns:read';

export interface McpSession {
  id: string;
  tenantId: string;
  userId: string;
  userEmail: string | null;
  clientName: string;
  clientType: string;
  deviceId: string | null;
  actorUid: string | null;
  actorKind: string | null;
  scopes: McpScope[];
  status: 'ACTIVE' | 'REVOKED' | string;
  tokenPrefix: string;
  createdAt: string;
  approvedAt: string | null;
  expiresAt: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
  metadata: Record<string, unknown>;
}

export interface DanhSachMcpSession {
  ok: boolean;
  sessions: McpSession[];
  total: number;
  allowedScopes: McpScope[];
}

export interface TaoMcpSessionInput {
  clientName: string;
  clientType: string;
  deviceId?: string | null;
  actorUid: string;
  actorKind?: string | null;
  scopes: McpScope[];
  ttlMs: number;
  note?: string;
}

export interface TaoMcpSessionResponse {
  ok: boolean;
  session: McpSession;
  token: string;
  tokenShownOnce: boolean;
  message: string;
}

export interface McpAudit {
  id: string;
  sessionId: string | null;
  tenantId: string;
  userId: string | null;
  actorUid: string | null;
  toolName: string;
  requestId: string | null;
  inputSummary: { keys?: string[]; [key: string]: unknown };
  resultStatus: 'OK' | 'ERROR' | string;
  resultCode: string | null;
  createdAt: string;
  durationMs: number | null;
  session: {
    id: string;
    clientName: string | null;
    clientType: string | null;
    tokenPrefix: string | null;
    status: string | null;
  } | null;
}

export interface DanhSachMcpAudit {
  ok: boolean;
  audits: McpAudit[];
  total: number;
}

export type McpWriteProposalStatus = 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' | 'EXPIRED' | 'CANCELLED' | string;

export interface McpWriteProposal {
  proposalId: string;
  tenantId: string;
  actorUid: string;
  actorKind: string | null;
  deviceId: string | null;
  sessionId: string;
  toolName: string;
  scope: string;
  status: McpWriteProposalStatus;
  inputSummary: { targetCount?: number; contentLength?: number; [key: string]: unknown };
  payloadPreview: { contentPreview?: string; content?: string; targets?: Array<{ type?: string; id?: string; name?: string }>; [key: string]: unknown };
  intentHash: string;
  idempotencyKey: string;
  expiresAt: string;
  createdByAiUserId: string;
  approvedByUserId: string | null;
  approvalOperationId: string;
  approvedJobId: string | null;
  approvedAt: string | null;
  rejectedByUserId: string | null;
  rejectedAt: string | null;
  rejectReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DanhSachMcpWriteProposal {
  ok: boolean;
  proposals: McpWriteProposal[];
  total: number;
}

export interface McpWriteProposalActionResponse {
  ok: boolean;
  proposalId: string;
  jobId?: string | null;
  status: McpWriteProposalStatus;
  queued?: boolean;
  replayed?: boolean;
  proposal: McpWriteProposal;
  message?: string;
}

export interface MucDanhBa {
  uid: string;
  name: string;
  avatar: string | null;
  tenantId: string | null;
  updatedAt: string;
  /**
   * true/false = biết chắc; null = không hỏi được trạng thái.
   * Phải phân biệt null với false: báo nhầm "offline" sẽ chặn oan tài
   * khoản đang chạy. Đây là lý do trường này thêm vào ngày 14/09/2026.
   */
  online: boolean | null;
}

export interface DanhBa {
  ok: boolean;
  accounts: MucDanhBa[];
  onlineKnown: boolean;
  onlineCount: number | null;
}

/**
 * Giá trị máy chủ trả khi câu hỏi KHÔNG ÁP DỤNG cho thực thể đó.
 * Ví dụ: số bạn bè của một Fanpage — Page không có bạn bè.
 *
 * Phải phân biệt với `0` (đã đo, thật sự bằng không) và `null` (chưa đo được).
 * Ba trạng thái dẫn tới ba hành động khác nhau của người dùng.
 */
export const KHONG_AP_DUNG = 'KHONG_AP_DUNG';

/** Số đếm có thể là: số thật, `null` (chưa rõ), hoặc `KHONG_AP_DUNG`. */
export type SoDem = number | null | typeof KHONG_AP_DUNG;

export interface MucTongQuanTK {
  uid: string;
  name: string | null;
  avatar: string | null;
  isPage: boolean;
  online: boolean;
  /** Vừa mất kết nối trong 24 giờ qua — khác với chưa từng kết nối. */
  vuaRot: boolean;
  onlineLuc: string | null;
  friendCount: SoDem;
  groupCount: SoDem;
  /** Mốc thời gian của số liệu; null nghĩa là chưa đo lần nào. */
  soLieuLuc: string | null;
  hoSoLuc: string | null;
}

export interface TongQuanTaiKhoan {
  ok: boolean;
  accounts: MucTongQuanTK[];
  refreshed: boolean;
  note?: string;
}

// ===== Nhóm =====
export interface Nhom {
  id: string;
  name: string;
  avatar: string;
  collection_name: string;
  url?: string;
  source?: string;
}

export interface BoSuuTapNhom {
  id: string;
  name: string;
  color: string;
  description: string;
  group_count?: number;
}

export interface JoinBatchItemDTO {
  id: string;
  groupId: string;
  status: string;
  jobId: string | null;
  nextEligibleAt?: string | null;
  errorCode?: string | null;
  message?: string | null;
}

export interface JoinBatchPolicyDTO {
  delayMinSec: number;
  delayMaxSec: number;
  roundSize: number;
  roundRestSec: number;
  dailyQuota?: number;
  dailyLimit?: number;
  dailyMax?: number;
  policyVersion?: number;
}

export interface GroupJoinPolicyDefaults {
  delayMinSec: number;
  delayMaxSec: number;
  roundSize: number;
  roundRestSec: number;
  dailyQuota: number;
  serverDailyMax: number;
}

export interface GroupJoinPolicyBounds {
  minDelaySec: number;
  maxDelaySec: number;
  minRoundSize: number;
  maxRoundSize: number;
  minRoundRestSec: number;
  maxRoundRestSec: number;
  minDailyQuota: number;
  maxDailyQuota: number;
}

export interface GroupJoinTechnicalAbsolutes {
  minDelaySec: number;
  maxDelaySec: number;
  minRoundSize: number;
  maxRoundSize: number;
  minRoundRestSec: number;
  maxRoundRestSec: number;
  minDailyQuota: number;
  maxDailyQuota: number;
  maxServerDailyMax: number;
}

export interface GroupJoinActorQuota {
  actorUid: string | null;
  todayUsage: number;
  remainingDaily: number | null;
  dateKey: string;
  effectiveDailyQuota: number | null;
  isBlocked: boolean;
  blockedReason: string | null;
}

export interface GroupJoinPolicyResponse {
  ok: boolean;
  version: number;
  defaults: GroupJoinPolicyDefaults;
  bounds: GroupJoinPolicyBounds;
  actorQuota: GroupJoinActorQuota;
  disclaimer?: string;
}

export interface UserJoinPolicyInput {
  delayMinMinutes: number | '';
  delayMaxMinutes: number | '';
  roundSize: number | '';
  roundRestMinutes: number | '';
  dailyLimit: number | '';
}

export interface UserJoinPolicyPayload {
  delayMinSec: number;
  delayMaxSec: number;
  roundSize: number;
  roundRestSec: number;
  dailyLimit: number;
}

export interface AdminJoinPolicyResponse {
  ok: boolean;
  version: number;
  policy: {
    defaults: GroupJoinPolicyDefaults;
    bounds: GroupJoinPolicyBounds;
  };
  technicalAbsolutes: GroupJoinTechnicalAbsolutes;
  updatedAt?: number;
  updatedBy?: string;
  updatedByEmail?: string;
}

export interface AdminJoinPolicyUpdateRequest {
  expectedVersion: number;
  defaults: GroupJoinPolicyDefaults;
  bounds: GroupJoinPolicyBounds;
  reason: string;
  operationKey?: string;
}

export interface AdminJoinPolicyAuditRecord {
  id: string;
  version: number;
  adminUserId?: string;
  adminEmail?: string;
  action: string;
  previousSettings: any;
  newSettings: any;
  reason: string;
  operationKey?: string;
  createdAt: number;
}

export interface AdminJoinPolicyAuditsResponse {
  ok: boolean;
  audits: AdminJoinPolicyAuditRecord[];
}

export interface JoinBatchDTO {
  id: string;
  status: string;
  actorUid: string;
  createdAt: string;
  nextEligibleAt?: string | null;
  policy: JoinBatchPolicyDTO;
  items: JoinBatchItemDTO[];
}

export interface KetQuaThamGiaNhom {
  ok: boolean;
  batchId?: string;
  status?: string;
  items?: JoinBatchItemDTO[];
  policy?: JoinBatchPolicyDTO;
  batch?: JoinBatchDTO;
  accepted?: boolean;
  message?: string;
  jobs?: Array<{ groupId: string; jobId: string | null; status: string }>;
  idempotentReplay?: boolean;
}

// ===== Chiến dịch =====
export type MediaMimeType = 'image/jpeg' | 'image/png' | 'image/webp' | 'video/mp4';

export interface LocalMediaRecord {
  localMediaId: string;
  name: string;
  mimeType: MediaMimeType;
  size: number;
  createdAt: number;
  lastUsedAt: number;
  status: 'AVAILABLE';
  storageMode: 'EXTENSION_OPFS';
}

export type TrangThaiJob =
  | 'PENDING' | 'PAUSED' | 'SUCCESS' | 'FAILED'
  | 'SKIPPED' | 'UNKNOWN_RESULT' | 'ABORTED' | 'CANCELLED' | 'DELETED'
  // Người dùng bấm chạy lại khiến backend trả trạng thái trung gian này trước khi job được xử lý lại.
  | 'RETRY_REQUESTED';

export interface Job {
  id: string;
  campaignId: string;
  tenantId: string;
  fbUid: string | null;
  flowId?: string;
  title: string;
  message: string;
  targetId: string;
  media: LocalMediaRecord[];
  backgroundId?: string | null;
  delayFromSec: number;
  delayToSec: number;
  firstComment: string | null;
  firstCommentJobId: string | null;
  useHighlight: boolean;
  // Số lượt bình luận đã trừ vào hạn mức gói khi tạo job. Chỉ job sinh từ
  // addCampaign mới có (jobStore.js), job sinh từ insert thì không.
  // Phát hiện khi đối chiếu kiểu này với dữ liệu THẬT trả về từ /api/fbi/jobs.
  commentQuotaUsed?: number;
  status: TrangThaiJob;
  createdAt: string;
  updatedAt: string;
  // Ba trường dưới chỉ xuất hiện trong một số trường hợp:
  //   evidenceUrl  - khi status SUCCESS và có đường dẫn bài đã đăng
  //   errorDetail  - khi FAILED / SKIPPED / UNKNOWN_RESULT
  //   fb_uid       - do route /jobs gắn thêm (jobs.routes.js:22-23), tên khác fbUid
  evidenceUrl?: string;
  errorDetail?: string;
  fb_uid?: string;
}

export interface ChienDich {
  campaignId: string;
  title: string;
  message: string;
  createdAt: string;
  fbUid: string | null;
  total: number;
  success: number;
  failed: number;
  pending: number;
  paused: number;
  aborted: number;
  skipped: number;
  unknown: number;
  deleted?: number;
}

// ===== AI =====
export interface TrangThaiAI {
  ok: boolean;
  available: boolean;
  /** Mã lý do khi không dùng được: AI_DISABLED | AI_NO_KEY | AI_NO_ENDPOINT */
  code: string | null;
  /** Câu giải thích cho người dùng đọc. Dùng làm tooltip khi nút bị làm mờ. */
  reason: string | null;
  provider: string;
  maxRewritesPerDay: number;
}

// ===== Lịch định kỳ =====
/**
 * Lịch đăng định kỳ.
 *
 * Bản trước khai báo `groupIds`, `time`, `enabled` — KHÔNG có trường nào trong số
 * đó tồn tại. Backend trả `targets`, `times`, `status` (xem hàm
 * `formatScheduleRow` trong lab-server/services/recurringScheduleStore.js).
 * Chỉ mục `[k: string]: unknown` che mất sai lệch nên TypeScript không báo.
 * Đã đối chiếu lại với cấu trúc thật và bỏ chỉ mục đó đi.
 */
export type TrangThaiLich = 'ACTIVE' | 'PAUSED' | 'COMPLETED';

export interface LichDinhKy {
  id: string;
  tenantId: string;
  fbUid: string;
  name: string | null;
  message: string;
  /** Danh sách ID nhóm đích. Backend không ràng buộc kiểu từng phần tử. */
  targets: string[];
  media: unknown[];
  /** Các khung giờ trong ngày, dạng "HH:mm". Bộ chạy nền so khớp đúng chuỗi này. */
  times: string[];
  isAnonymous: boolean;
  safeMode: boolean;
  delayFromSec: number;
  delayToSec: number;
  /** Dạng "YYYY-MM-DD". Ngoài khoảng này thì lịch không chạy. */
  startDate: string | null;
  endDate: string | null;
  /**
   * COMPLETED do chính bộ chạy nền đặt khi quá `endDate` —
   * người dùng chỉ bật/tắt được giữa ACTIVE và PAUSED.
   */
  status: TrangThaiLich;
  /** Mốc mili-giây của lần chạy gần nhất. */
  lastRunAt: number | null;
  /** Khung giờ đã chạy gần nhất, dạng "YYYY-MM-DD_HH:mm" — dùng để không chạy trùng. */
  lastRunSlot: string | null;
  createdAt: number;
  updatedAt: number;
}

// ===== Bình luận =====
export interface BaiDeBinhLuan {
  /** ID bài viết Facebook. Luôn giữ dạng chuỗi vì ID Facebook có thể vượt MAX_SAFE_INTEGER. */
  postId: string;
  url: string;
  campaignId: string | null;
  /** ID nhóm nơi bài đã được đăng. Chỉ dùng để hiển thị, KHÔNG gửi thay postId. */
  groupId: string | null;
  message: string;
  createdAt: string | number | null;
}

export interface BoCauBinhLuan {
  id: string;
  name: string;
  items: string[];
  count: number;
  updatedAt: number;
}

export interface KetQuaGuiBinhLuan {
  ok: boolean;
  jobId: string;
  jobIds: string[];
  total: number;
  failed: number;
  failures?: Array<{ postId: string; reason: string }>;
  message: string;
}

// ===== Lỗi chung =====
export interface LoiAPI {
  ok: false;
  code?: string;
  message?: string;
  data?: Record<string, unknown>;
}

// ===== Khuôn payload GraphQL (chỉ Admin) =====

/**
 * Một bản khuôn đã nộp.
 *
 * `isActive` là thứ quan trọng nhất: nộp xong khuôn nằm im, phải kích hoạt
 * riêng. Khuôn sai không hỏng một job — nó hỏng MỌI job của MỌI khách.
 */
export interface KhuonPayload {
  id: string;
  templateKey: string;
  flowId: string;
  docId: string;
  friendlyName: string;
  basePayload: Record<string, unknown> | null;
  fingerprints: Record<string, string[]> | null;
  version: number;
  isActive: boolean;
  ghiChu: string | null;
  nguoiNop: string | null;
  createdAt: number;
  updatedAt: number;
}

/** Một dòng trong ma trận: loại khuôn nào đã có, loại nào còn trống. */
export interface DongMaTranKhuon {
  templateKey: string;
  soBan: number;
  banMoiNhat: number | null;
  dangChay: number;
}

export interface MaTranKhuon {
  ok: boolean;
  matrix: DongMaTranKhuon[];
  tong: number;
  daPhuSong: string[];
  conTrong: string[];
}

/** Chênh lệch giữa khuôn mới nộp và bản đang chạy cùng loại. */
export interface ChenhLechKhuon {
  themMoi: string[];
  biXoa: string[];
  doiGiaTri: Array<{ duong: string; cu: unknown; moi: unknown }>;
  giongNhau: boolean;
}

// ===== Action Catalog (Approved Declarative Scenarios) =====

export type ActionScenarioStatus = 'DRAFT' | 'READY' | 'VERIFIED' | 'BLOCKED';

export type ActionVerificationStatus = 'UNVERIFIED' | 'REPORTED' | 'CONFIRMED';

export type ActionVerificationEnvironment = 'local' | 'staging' | 'production';

export type ActionVerificationProvenance = 'CALLER_REPORTED';

export type ActionExecutionMode = 'EXISTING_PRODUCT_UI';

export type ActionExecutionScreen = 'tao-bai' | 'binh-luan';

export interface ActionScenarioVerification {
  status: ActionVerificationStatus;
  verifiedAt: string | null;
  summary: string | null;
  sourceRevision: string | null;
  extensionVersion: string | null;
  environment: ActionVerificationEnvironment | null;
  provenance: ActionVerificationProvenance | null;
}

export interface ActionScenarioExecution {
  mode: ActionExecutionMode;
  screen: ActionExecutionScreen;
  available: false;
  reason: string | null;
}

export interface ActionScenario {
  scenarioId: string;
  capabilityKey: string;
  displayName: string;
  description: string;
  status: ActionScenarioStatus;
  verification: ActionScenarioVerification;
  execution: ActionScenarioExecution;
}

export interface ActionCatalogResponse {
  ok: boolean;
  scenarios: ActionScenario[];
}

// ===== Background Presets (Server-Authoritative Catalog) =====

export type BackgroundPresetPreviewType = 'solid' | 'gradient' | 'image';

export interface BackgroundPresetPreview {
  type: BackgroundPresetPreviewType;
  colors?: string[];
  angle?: number;
  assetUrl?: string;
  fallbackColor?: string;
}

export type BackgroundPresetStatus = 'DISCOVERED' | 'ENABLED' | 'VERIFIED';

export type BackgroundPresetScope = 'GROUP_PERSONAL';

export interface BackgroundPresetItem {
  id: string;
  label: string;
  preview: BackgroundPresetPreview | null;
  status: BackgroundPresetStatus;
  enabled: boolean;
  scope: BackgroundPresetScope;
}

export interface BackgroundPresetsResponse {
  ok: boolean;
  presets: BackgroundPresetItem[];
}

// ===== Khả năng sản phẩm (Authoritative Product Capabilities) =====

export interface ProductCapabilityItem {
  enabled: boolean;
  planned: boolean;
  reason: string;
}

export type ProductCapabilitiesMap = Record<string, ProductCapabilityItem>;

export interface ProductCapabilitiesResponse {
  ok: boolean;
  capabilities: ProductCapabilitiesMap;
}

// ===== Màn hình điều hướng =====
export type { ManHinh } from '../components/ThanhBen';
